/** First non-empty string. Empty string must never count as a scriptId. */
export function pickScriptId(
  ...candidates: Array<string | null | undefined>
): string | undefined {
  return candidates.find((value): value is string => typeof value === "string" && value.length > 0)
}

/** Normalize GET workspaces/:id/owner-script-id JSON to a real loader id. */
export function scriptIdFromOwnerResponse(payload: unknown): string | undefined {
  if (!payload || typeof payload !== "object") return undefined
  const record = payload as Record<string, unknown>
  return pickScriptId(
    typeof record.scriptId === "string" ? record.scriptId : undefined,
    typeof record.ownerScriptId === "string" ? record.ownerScriptId : undefined,
    typeof record.id === "string" ? record.id : undefined,
  )
}

/** Humblytics GTM Community Template — exact name as published in the gallery. */
export const GTM_TEMPLATE_NAME = "Humblytics"

/**
 * Direct link to the Humblytics GTM Community Template in the Tag Manager
 * gallery. Update this if/when the template URL changes.
 */
export const GTM_GALLERY_URL =
  "https://tagmanager.google.com/gallery/#/?filter=humblytics"

/**
 * Install snippet with anti-flicker. `k` must match `hmblPathKey` in the backend's
 * utils/split-redirect-runtime.js. `_st` is only stripped before any pixel loads,
 * since a loaded pixel counts the URL change as a second page view.
 */
export function getTrackingSnippet(scriptId: string): string {
  return `<!-- Start Humblytics Tracking Code -->
<style>html.hmbl-pending body{opacity:0!important}</style>
<script>
  (function(){
    var w=window,l=location,d=document.documentElement,done=false;
    function k(p){p=String(p||'/').toLowerCase();while(p.length>1&&p.charAt(p.length-1)==='/')p=p.slice(0,-1);for(var h=5381,i=0;i<p.length;i++)h=((h*33)^p.charCodeAt(i))>>>0;return h.toString(36);}
    function r(){if(done)return;done=true;d.classList.remove('hmbl-pending');if(d.style.opacity==='0')d.style.opacity='';}
    w._hmblReveal=r;
    if(document.readyState!=='loading'||/[?&](hmblEditor|hmblSessionRecording|humblytics_heatmap_token)=|#preview-humblytics-/.test(l.search+l.hash))return;
    var st=new URLSearchParams(l.search).get('_st'),ref=document.referrer,same=!ref;
    try{same=same||new URL(ref).host===l.host;}catch(e){same=true;}
    if(st&&same&&st===k(l.pathname)){
      w._hmblSt=st;
      if(!(w.fbq||w.google_tag_manager||w.ttq)){var u=new URL(l.href);u.searchParams.delete('_st');history.replaceState(history.state,'',u.href);}
      return;
    }
    d.classList.add('hmbl-pending');
    function cap(){setTimeout(function(){if(!w.HumblyticsRedirectRunning)r();},2000);setTimeout(r,4000);}
    if(document.prerendering)document.addEventListener('prerenderingchange',cap,{once:true});else cap();
    w.addEventListener('pageshow',function(e){if(e.persisted)r();});
  })();
</script>
<script async src="https://app.humblytics.com/hmbl.min.js?id=${scriptId}" onerror="window._hmblReveal&&window._hmblReveal()"></script>
<!-- End Humblytics Tracking Code -->`
}

/**
 * Minimal loader-only snippet (no FOUC reveal).
 * Used by GeneralTab in site settings.
 */
function getSimpleTrackingSnippet(scriptId: string): string {
  return `<!-- Humblytics Analytics -->
<script async src="https://app.humblytics.com/hmbl.min.js?id=${scriptId}"></script>`
}
